// @ts-nocheck
import { useRef, useEffect } from 'react';

export default function SpaceBackgroundSimple() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let W: number, H: number;
    let stars: any[] = [];
    let shoots: any[] = [];
    let galaxies: any[] = [];
    let frame = 0;
    let shootTimer = 0;
    let lmx = -999;
    let lmy = -999;
    let animationFrameId: number;

    const nebCfg = [
      { cx: 0.12, cy: 0.20, blobs: [[0, 0, 0.26, [165, 18, 88, 0.26]], [0.07, 0.05, 0.15, [215, 50, 68, 0.18]], [-0.05, 0.09, 0.11, [255, 90, 140, 0.12]]] },
      { cx: 0.80, cy: 0.26, blobs: [[0, 0, 0.24, [15, 52, 190, 0.24]], [-0.08, 0.06, 0.14, [30, 100, 230, 0.16]], [0.06, -0.07, 0.10, [65, 150, 255, 0.10]]] },
      { cx: 0.52, cy: 0.87, blobs: [[0, 0, 0.22, [30, 118, 95, 0.20]], [0.09, -0.05, 0.13, [15, 158, 128, 0.14]], [-0.06, 0.04, 0.09, [60, 200, 160, 0.09]]] },
      { cx: 0.90, cy: 0.66, blobs: [[0, 0, 0.20, [82, 8, 165, 0.22]], [-0.06, 0.07, 0.12, [122, 20, 208, 0.16]], [0.04, -0.05, 0.08, [160, 60, 240, 0.10]]] },
      { cx: 0.28, cy: 0.74, blobs: [[0, 0, 0.18, [8, 78, 162, 0.18]], [0.06, -0.04, 0.11, [20, 120, 205, 0.12]]] },
      { cx: 0.60, cy: 0.10, blobs: [[0, 0, 0.18, [190, 48, 10, 0.18]], [-0.07, 0.06, 0.11, [230, 85, 22, 0.12]], [0.05, -0.02, 0.08, [255, 140, 50, 0.08]]] },
      { cx: 0.42, cy: 0.50, blobs: [[0, 0, 0.14, [100, 20, 180, 0.10]], [0.06, 0.05, 0.09, [60, 10, 140, 0.07]]] }
    ];

    const galDefs = [
      { cx: 0.74, cy: 0.14, rf: 0.105, flatY: 0.28, angle: -0.38, arms: 2, r0: 148, g0: 188, b0: 255, type: 'spiral' },
      { cx: 0.13, cy: 0.52, rf: 0.064, flatY: 0.32, angle: 1.22, arms: 2, r0: 255, g0: 212, b0: 125, type: 'barred' },
      { cx: 0.87, cy: 0.45, rf: 0.058, flatY: 0.44, angle: 0.65, arms: 5, r0: 172, g0: 148, b0: 255, type: 'flocculent' },
      { cx: 0.37, cy: 0.07, rf: 0.055, flatY: 0.11, angle: -1.05, arms: 2, r0: 255, g0: 190, b0: 145, type: 'edge_on' },
      { cx: 0.54, cy: 0.64, rf: 0.050, flatY: 0.56, angle: 0.90, arms: 2, r0: 255, g0: 228, b0: 168, type: 'elliptical' },
      { cx: 0.06, cy: 0.83, rf: 0.054, flatY: 0.50, angle: 1.82, arms: 2, r0: 80, g0: 200, b0: 210, type: 'irregular' },
      { cx: 0.83, cy: 0.83, rf: 0.048, flatY: 0.40, angle: -0.52, arms: 2, r0: 200, g0: 176, b0: 255, type: 'ring' },
    ];

    function makeGalParticles(r: number, arms: number, type: string) {
      const ps: any[] = [];
      const n = Math.floor(r * 3.2);

      if (type === 'elliptical') {
        for (let i = 0; i < n; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 0.38) * r * 0.88;
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.52 + 0.12,
            alpha: (1 - d / (r * 0.88)) * 0.82 * (Math.random() * 0.30 + 0.48),
            warm: true
          });
        }
      } else if (type === 'barred') {
        const barLen = r * 0.44;
        for (let i = 0; i < Math.floor(n * 0.28); i++) {
          const bx = (Math.random() * 2 - 1) * barLen;
          const by = (Math.random() - 0.5) * r * 0.11;
          ps.push({
            x: bx,
            y: by,
            r: Math.random() * 0.50 + 0.14,
            alpha: (0.36 + 0.56 * (1 - Math.abs(bx) / barLen)) * (Math.random() * 0.28 + 0.52),
            warm: Math.abs(bx) < barLen * 0.5
          });
        }
        for (let i = 0; i < Math.floor(n * 0.72); i++) {
          const side = i % 2 === 0 ? 1 : -1;
          const baseAng = side > 0 ? 0 : Math.PI;
          const t = Math.random() * Math.PI * 1.75;
          const d = (t / (Math.PI * 1.75)) * (r - barLen * 0.72) + barLen * 0.72;
          const ang = baseAng + t * 1.55 + (Math.random() - 0.5) * 0.40;
          const sd = (Math.random() - 0.5) * r * 0.07;
          const sa = ang + Math.PI / 2;
          ps.push({
            x: Math.cos(ang) * d + Math.cos(sa) * sd,
            y: Math.sin(ang) * d + Math.sin(sa) * sd,
            r: Math.random() * 0.55 + 0.12,
            alpha: Math.max(0.06, 1 - d / r * 0.64) * (Math.random() * 0.40 + 0.22),
            warm: d < r * 0.42
          });
        }
        for (let i = 0; i < 20; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 2) * r * 0.14;
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.44 + 0.13,
            alpha: 0.62 + Math.random() * 0.30,
            warm: true
          });
        }
      } else if (type === 'flocculent') {
        const na = 5;
        for (let i = 0; i < n; i++) {
          const arm = i % na;
          const off = (arm / na) * Math.PI * 2;
          const t = Math.random() * Math.PI * 1.55;
          const d = (t / (Math.PI * 1.55)) * r * (0.62 + Math.random() * 0.38);
          const ang = off + t * 1.32 + (Math.random() - 0.5) * 1.05;
          const sd = (Math.random() - 0.5) * r * 0.17;
          const sa = ang + Math.PI / 2;
          ps.push({
            x: Math.cos(ang) * d + Math.cos(sa) * sd,
            y: Math.sin(ang) * d + Math.sin(sa) * sd,
            r: Math.random() * 0.54 + 0.12,
            alpha: Math.max(0.05, 0.80 - d / r * 0.66) * (Math.random() * 0.48 + 0.15),
            warm: d < r * 0.30
          });
        }
        for (let i = 0; i < 20; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 2) * r * 0.16;
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.44 + 0.13,
            alpha: 0.65 + Math.random() * 0.28,
            warm: true
          });
        }
      } else if (type === 'edge_on') {
        for (let i = 0; i < n; i++) {
          const x = (Math.random() * 2 - 1) * r;
          const y = (Math.random() - 0.5) * r * 0.55;
          const d = Math.abs(x);
          ps.push({
            x,
            y,
            r: Math.random() * 0.55 + 0.12,
            alpha: Math.max(0.04, (1 - d / r * 0.70) * 0.90) * (Math.random() * 0.35 + 0.26),
            warm: d < r * 0.20
          });
        }
        for (let i = 0; i < 20; i++) {
          const x = (Math.random() - 0.5) * r * 0.28;
          const y = (Math.random() - 0.5) * r * 0.28;
          ps.push({
            x,
            y,
            r: Math.random() * 0.44 + 0.13,
            alpha: 0.60 + Math.random() * 0.32,
            warm: true
          });
        }
      } else if (type === 'irregular') {
        const nc = 5 + Math.floor(Math.random() * 4);
        const clumps: any[] = [];
        for (let c = 0; c < nc; c++) {
          clumps.push({
            x: (Math.random() * 2 - 1) * r * 0.66,
            y: (Math.random() * 2 - 1) * r * 0.50,
            size: r * (0.14 + Math.random() * 0.26),
            bright: Math.random()
          });
        }
        for (let i = 0; i < n; i++) {
          const cl = clumps[i % clumps.length];
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 0.60) * cl.size;
          ps.push({
            x: cl.x + Math.cos(a) * d,
            y: cl.y + Math.sin(a) * d,
            r: Math.random() * 0.58 + 0.13,
            alpha: (0.26 + cl.bright * 0.44) * (Math.random() * 0.44 + 0.26),
            warm: cl.bright > 0.56
          });
        }
      } else if (type === 'ring') {
        const iR = r * 0.46;
        const oR = r * 0.90;
        const mid = (iR + oR) / 2;
        const hw = (oR - iR) / 2;
        for (let i = 0; i < Math.floor(n * 0.76); i++) {
          const a = Math.random() * Math.PI * 2;
          const d = iR + Math.random() * (oR - iR);
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.56 + 0.14,
            alpha: (0.32 + 0.58 * (1 - Math.abs(d - mid) / hw)) * (Math.random() * 0.32 + 0.28),
            warm: false
          });
        }
        for (let i = 0; i < Math.floor(n * 0.24); i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 1.5) * iR * 0.50;
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.46 + 0.13,
            alpha: 0.50 + Math.random() * 0.44,
            warm: true
          });
        }
        for (let i = 0; i < 6; i++) {
          const sa = i / 6 * Math.PI * 2;
          for (let j = 0; j < 4; j++) {
            const d = iR * 0.55 + j * (iR * 0.45) / 4;
            ps.push({
              x: Math.cos(sa) * d,
              y: Math.sin(sa) * d,
              r: 0.22,
              alpha: 0.15 + j * 0.04,
              warm: false
            });
          }
        }
      } else {
        // Default spiral
        for (let i = 0; i < n; i++) {
          const arm = i % arms;
          const off = (arm / arms) * Math.PI * 2;
          const t = Math.random() * Math.PI * 2.1;
          const d = (t / (Math.PI * 2.1)) * r;
          const ang = off + t * 1.65 + (Math.random() - 0.5) * 0.48;
          const sd = (Math.random() - 0.5) * r * 0.09;
          const sa = ang + Math.PI / 2;
          ps.push({
            x: Math.cos(ang) * d + Math.cos(sa) * sd,
            y: Math.sin(ang) * d + Math.sin(sa) * sd,
            r: Math.random() * 0.58 + 0.13,
            alpha: Math.max(0.07, 1 - d / r * 0.62) * (Math.random() * 0.46 + 0.20),
            warm: d < r * 0.32
          });
        }
        for (let i = 0; i < 22; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 2) * r * 0.18;
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.44 + 0.13,
            alpha: 0.70 + Math.random() * 0.28,
            warm: true
          });
        }
      }
      return ps;
    }

    function initGalaxies() {
      galaxies = galDefs.map(def => {
        const r = def.rf * Math.min(W, H);
        return { ...def, r, particles: makeGalParticles(r, def.arms, def.type) };
      });
    }

    const starVariants = [
      [0, 0, 100, 0.12, 0.80, 0.28, false], [0, 0, 100, 0.12, 0.80, 0.28, false],
      [208, 82, 96, 0.22, 1.10, 0.33, false], [46, 92, 90, 0.28, 1.20, 0.36, false],
      [24, 88, 83, 0.30, 1.25, 0.33, false], [350, 72, 80, 0.28, 1.10, 0.31, false],
      [280, 60, 88, 0.20, 1.00, 0.30, false], [168, 65, 88, 0.22, 1.00, 0.30, false],
      [0, 0, 100, 1.7, 3.0, 0.80, true], [208, 78, 96, 1.8, 3.2, 0.82, true],
      [46, 88, 92, 1.6, 2.8, 0.80, true], [24, 82, 85, 1.5, 2.5, 0.75, true], [350, 70, 82, 1.5, 2.4, 0.72, true],
    ];

    const starW = [0.296, 0.167, 0.147, 0.101, 0.073, 0.046, 0.037, 0.037, 0.037, 0.022, 0.015, 0.015, 0.007];

    let initialized = false;
    // Lock to initial viewport size to prevent iOS address bar resize jitter
    const initialW = window.innerWidth;
    const initialH = window.innerHeight;
    function resize() {
      W = canvas.width = initialW;
      H = canvas.height = initialH;
      // Only create stars/galaxies once — don't re-randomize on resize/scroll
      if (!initialized) {
        makeStars();
        initGalaxies();
        initialized = true;
      }
    }

    function makeStars() {
      stars = [];
      const cw: number[] = [];
      let s = 0;
      for (const w of starW) {
        s += w;
        cw.push(s);
      }
      for (let i = 0; i < 585; i++) {
        const rv = Math.random();
        let vi = 0;
        for (let j = 0; j < cw.length; j++) {
          if (rv < cw[j]) {
            vi = j;
            break;
          }
        }
        const v = starVariants[vi];
        const r = v[3] + Math.random() * (v[4] - v[3]);
        stars.push({
          x: Math.random() * W,
          y: Math.random() * H,
          r,
          base: v[5] + Math.random() * (1 - v[5]) * 0.4,
          ts: Math.random() * 0.022 + 0.003,
          to: Math.random() * Math.PI * 2,
          hue: v[0],
          sat: v[1],
          lit: v[2],
          giant: v[6]
        });
      }
    }

    function spawnShoot(x: number | null, y: number | null, burst: boolean) {
      const n = burst ? 3 + Math.floor(Math.random() * 2) : 1;
      for (let c = 0; c < n; c++) {
        setTimeout(() => {
          const a = Math.PI * 0.3 + (Math.random() - 0.5) * (burst ? 1.8 : 0.5);
          const spd = burst ? 4 + Math.random() * 5 : 7 + Math.random() * 6;
          shoots.push({
            x: (x ?? Math.random() * W * 0.8) + (burst ? (Math.random() - 0.5) * 14 : 0),
            y: (y ?? Math.random() * H * 0.4) + (burst ? (Math.random() - 0.5) * 14 : 0),
            vx: Math.cos(a) * spd,
            vy: Math.sin(a) * spd,
            trail: [],
            tl: 20 + Math.floor(Math.random() * 18),
            life: 1,
            decay: 0.011 + Math.random() * 0.007,
            fromMouse: x != null
          });
        }, burst ? c * 100 : 0);
      }
    }

    function drawNebulae() {
      for (const neb of nebCfg) {
        for (const [dx, dy, rr, rgba] of neb.blobs) {
          const gx = (neb.cx + dx) * W;
          const gy = (neb.cy + dy) * H;
          const gr = rr * Math.min(W, H);
          const g = ctx.createRadialGradient(gx, gy, 0, gx, gy, gr);
          g.addColorStop(0, `rgba(${rgba[0]},${rgba[1]},${rgba[2]},${rgba[3]})`);
          g.addColorStop(0.42, `rgba(${rgba[0]},${rgba[1]},${rgba[2]},${(rgba[3] * 0.42).toFixed(3)})`);
          g.addColorStop(1, `rgba(${rgba[0]},${rgba[1]},${rgba[2]},0)`);
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(gx, gy, gr, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    function drawGalaxies() {
      for (const gal of galaxies) {
        const gx = gal.cx * W;
        const gy = gal.cy * H;
        ctx.save();
        ctx.translate(gx, gy);
        ctx.rotate(gal.angle);

        const mg = ctx.createRadialGradient(0, 0, 0, 0, 0, gal.r * 0.82);
        mg.addColorStop(0, `rgba(${gal.r0},${gal.g0},${gal.b0},0.16)`);
        mg.addColorStop(0.55, `rgba(${gal.r0},${gal.g0},${gal.b0},0.05)`);
        mg.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = mg;
        ctx.beginPath();
        ctx.ellipse(0, 0, gal.r * 0.82, gal.r * 0.82 * gal.flatY, 0, 0, Math.PI * 2);
        ctx.fill();

        const wr = Math.min(255, gal.r0 + 28);
        const wg = Math.min(255, gal.g0 + 28);
        const wb = Math.min(255, gal.b0 + 52);

        for (const p of gal.particles) {
          ctx.globalAlpha = p.alpha;
          ctx.fillStyle = p.warm
            ? `rgb(${gal.r0},${gal.g0},${Math.floor(gal.b0 * 0.72 + 46)})`
            : `rgb(${wr},${wg},${wb})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y * gal.flatY, p.r, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.globalAlpha = 1;

        if (gal.type !== 'irregular') {
          const coreR = gal.type === 'ring' ? gal.r * 0.14 : gal.type === 'edge_on' ? gal.r * 0.18 : gal.r * 0.26;
          const flatClamp = gal.type === 'edge_on' ? Math.max(0.55, gal.flatY * 4.5) : Math.max(0.40, gal.flatY * 1.55);
          const cg = ctx.createRadialGradient(0, 0, 0, 0, 0, coreR);
          cg.addColorStop(0, 'rgba(255,252,235,0.96)');
          cg.addColorStop(0.32, `rgba(${gal.r0},${gal.g0},${gal.b0},0.66)`);
          cg.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = cg;
          ctx.beginPath();
          ctx.ellipse(0, 0, coreR, coreR * flatClamp, 0, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      }
    }

    function loop() {
      frame++;
      animationFrameId = requestAnimationFrame(loop);

      ctx.fillStyle = '#020810';
      ctx.fillRect(0, 0, W, H);

      drawNebulae();
      drawGalaxies();

      for (const s of stars) {
        const tw = Math.sin(frame * s.ts + s.to) * 0.2 + 0.8;
        const al = s.base * tw;
        const col = s.sat > 0 ? `hsl(${s.hue},${s.sat}%,${s.lit}%)` : '#fff';
        ctx.globalAlpha = al;
        ctx.fillStyle = col;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();

        if (s.giant && s.r > 1.5) {
          const sl = s.r * 3.5;
          ctx.strokeStyle = col;
          ctx.lineWidth = s.r * 0.20;
          ctx.globalAlpha = al * 0.28;
          ctx.beginPath();
          ctx.moveTo(s.x - sl, s.y);
          ctx.lineTo(s.x + sl, s.y);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(s.x, s.y - sl);
          ctx.lineTo(s.x, s.y + sl);
          ctx.stroke();

          ctx.globalAlpha = al * 0.10;
          ctx.lineWidth = s.r * 0.14;
          const dl = sl * 0.55;
          ctx.beginPath();
          ctx.moveTo(s.x - dl, s.y - dl);
          ctx.lineTo(s.x + dl, s.y + dl);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(s.x + dl, s.y - dl);
          ctx.lineTo(s.x - dl, s.y + dl);
          ctx.stroke();
        }
      }

      ctx.globalAlpha = 1;

      shootTimer++;
      if (shootTimer > 185 + Math.random() * 255) {
        spawnShoot(null, null, false);
        if (Math.random() < 0.38) {
          setTimeout(() => spawnShoot(null, null, false), 260 + Math.random() * 500);
        }
        shootTimer = 0;
      }

      for (let i = shoots.length - 1; i >= 0; i--) {
        const s = shoots[i];
        s.trail.unshift({ x: s.x, y: s.y });
        if (s.trail.length > s.tl) s.trail.pop();
        s.x += s.vx;
        s.y += s.vy;
        s.life -= s.decay;

        if (s.life <= 0 || s.x > W + 250 || s.y > H + 250 || s.x < -250) {
          shoots.splice(i, 1);
          continue;
        }

        for (let j = 1; j < s.trail.length; j++) {
          const t = 1 - j / s.trail.length;
          ctx.strokeStyle = s.fromMouse
            ? `rgba(175,208,255,${t * t * s.life * 0.88})`
            : `rgba(255,255,255,${t * t * s.life * 0.88})`;
          ctx.lineWidth = t * 2.8;
          ctx.beginPath();
          ctx.moveTo(s.trail[j - 1].x, s.trail[j - 1].y);
          ctx.lineTo(s.trail[j].x, s.trail[j].y);
          ctx.stroke();
        }

        ctx.fillStyle = s.fromMouse
          ? `rgba(200,220,255,${s.life})`
          : `rgba(255,255,255,${s.life})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function getPos(cx: number, cy: number) {
      const r = canvas.getBoundingClientRect();
      return {
        x: (cx - r.left) * (W / r.width),
        y: (cy - r.top) * (H / r.height)
      };
    }

    const handleClick = (e: MouseEvent) => {
      const p = getPos(e.clientX, e.clientY);
      spawnShoot(p.x, p.y, true);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const p = getPos(e.clientX, e.clientY);
      if (Math.hypot(p.x - lmx, p.y - lmy) > 45) {
        spawnShoot(p.x, p.y, false);
        lmx = p.x;
        lmy = p.y;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const p = getPos(e.touches[0].clientX, e.touches[0].clientY);
      spawnShoot(p.x, p.y, true);
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const p = getPos(e.touches[0].clientX, e.touches[0].clientY);
      if (Math.hypot(p.x - lmx, p.y - lmy) > 45) {
        spawnShoot(p.x, p.y, false);
        lmx = p.x;
        lmy = p.y;
      }
    };

    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('resize', resize);

    resize();
    loop();

    return () => {
      cancelAnimationFrame(animationFrameId);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        id="space-canvas-simple"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
    </>
  );
}
